import React, { useState, useEffect } from "react";
import { db, auth } from "../firebaseConfig";
import { doc, getDoc, collection, addDoc, getDocs, updateDoc } from "firebase/firestore";
import { Button, Row, Col, Card, Form, Container, ProgressBar, Alert, Badge, FormControl } from "react-bootstrap";
import Header from "../layouts/Header";

export default function SignedDocuments() {
  const [documents, setDocuments] = useState([]);
  const [unsignedFile, setUnsignedFile] = useState(null);
  const [signedFile, setSignedFile] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [createdForEmail, setCreatedForEmail] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [canSendSignRequests, setCanSendSignRequests] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const [createdByFilter, setCreatedByFilter] = useState("");
  const [createdForFilter, setCreatedForFilter] = useState("");

  useEffect(() => {
    const fetchUserRole = async () => {
      const user = auth.currentUser;
      if (user) {
        setEmail(user.email);
        const userDocRef = doc(db, "Users", user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userRole = userDoc.data().role || "user";
          setRole(userRole);
          await fetchRolePermissions(userRole); // Fetch permissions based on role
        }
      }
    };

    fetchUserRole();
    fetchDocuments();
  }, []);

  const fetchRolePermissions = async (userRole) => {
    const rolesDocRef = collection(db, "Roles");
    const rolesSnapshot = await getDocs(rolesDocRef);
    const roles = rolesSnapshot.docs.map((doc) => doc.data());

    const matchedRole = roles.find((role) => role.name === userRole);
    if (matchedRole && matchedRole.permissions) {
      setCanSendSignRequests(matchedRole.permissions.canSendSignRequests || false);
    }
  };

  const fetchDocuments = async () => {
    const documentSnapshot = await getDocs(collection(db, "SignedDocuments"));
    const allDocuments = documentSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    const userEmail = auth.currentUser.email;
    const filteredDocuments =
      role === "admin"
        ? allDocuments
        : allDocuments.filter(
            (doc) => doc.createdBy.email === userEmail || doc.createdFor === userEmail
          );

    setDocuments(filteredDocuments);
  };

  const handleFileUpload = (e, setFile) => {
    setFile(e.target.files[0]);
  };

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    const originalName = file.name.replace(/\.\w+$/, ""); // Remove any existing extensions
    formData.append("file", file);
    formData.append("public_id", originalName); // Use clean name
    formData.append("upload_preset", "documents");

    const response = await fetch("https://api.cloudinary.com/v1_1/dwcbtliac/upload", {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Failed to upload file to Cloudinary");
    }

    const data = await response.json();
    console.log("Cloudinary Response:", data);

    // Ensure URL consistency
    const cleanUrl = data.secure_url.replace(/\.docx\.docx$/, ".docx");
    console.log("Clean URL:", cleanUrl);

    return cleanUrl; // Return the processed URL
  };

  const handleUpload = async () => {
    if (!unsignedFile || !createdForEmail || !name || !description) {
      alert("Please fill in all required fields (file, recipient email, name, description).");
      return;
    }

    setUploading(true);

    try {
      const unsignedUrl = await uploadToCloudinary(unsignedFile);

      const documentRef = collection(db, "SignedDocuments");
      await addDoc(documentRef, {
        name,
        description,
        unsignedUrl,
        signedUrl: null,
        signedStatus: "Pending",
        createdAt: new Date(),
        createdBy: { email },
        createdFor: createdForEmail,
      });

      setUnsignedFile(null);
      setName("");
      setDescription("");
      setCreatedForEmail("");
      setUploading(false);
      fetchDocuments();
    } catch (error) {
      console.error("File upload failed:", error);
      setUploading(false);
    }
  };

  const handleSignedUpload = async (docId) => {
    if (!signedFile) {
      alert("Please select a signed document.");
      return;
    }

    setUploading(true);

    try {
      const signedUrl = await uploadToCloudinary(signedFile);

      const documentRef = doc(db, "SignedDocuments", docId);
      await updateDoc(documentRef, {
        signedUrl,
        signedStatus: "Completed",
      });

      setSignedFile(null);
      setUploading(false);
      fetchDocuments();
    } catch (error) {
      console.error("File upload failed:", error);
      setUploading(false);
    }
  };

  const filteredDocuments = documents.filter((doc) => {
    const documentName = doc.name || ""; // Default to empty string if name is undefined
    const createdByEmail = doc.createdBy.email || "";
    const createdForEmail = doc.createdFor || "";

    return (
      documentName.toLowerCase().includes(filterQuery) &&
      createdByEmail.toLowerCase().includes(createdByFilter) &&
      createdForEmail.toLowerCase().includes(createdForFilter)
    );
  });

  const pendingDocuments = filteredDocuments.filter((doc) => doc.signedStatus !== "Completed");
  const completedDocuments = filteredDocuments.filter((doc) => doc.signedStatus === "Completed");

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Enhanced Header Section */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="ri-file-signature-line fs-1 text-primary me-3"></i>
            <h1 className="display-6 fw-bold text-primary mb-0">Signed Documents</h1>
          </div>
          <p className="lead text-muted">Manage document signatures and track signing progress</p>
        </div>

        {/* Enhanced Alert Cards */}
        <Row className="mb-5">
          <Col xs={12} lg={6}>
            <div className="bg-gradient-primary text-white rounded-4 p-4 shadow-lg mb-3"
                 style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <div className="d-flex align-items-center">
                <i className="ri-information-line fs-4 me-3"></i>
                <div>
                  <h6 className="mb-1 fw-bold">Access Level</h6>
                  <p className="mb-0 small">
                    {role === "admin"
                      ? "You are logged in as an admin and can view all signed document requests."
                      : "You can view and manage document requests created by you or assigned to you."}
                  </p>
                </div>
              </div>
            </div>
          </Col>
          {(role === "admin" || canSendSignRequests) && (
            <Col xs={12} lg={6}>
              <div className="bg-gradient-success text-white rounded-4 p-4 shadow-lg mb-3"
                   style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-check-line fs-4 me-3"></i>
                  <div>
                    <h6 className="mb-1 fw-bold">Signature Access</h6>
                    <p className="mb-0 small">You have access to send signature requests.</p>
                  </div>
                </div>
              </div>
            </Col>
          )}
        </Row>

        {/* Enhanced Signature Request Form */}
        {(role === "admin" || canSendSignRequests) && (
          <Card className="border-0 shadow-lg rounded-4 mb-5">
            <div className="bg-gradient-info text-white p-4 rounded-top-4" 
                 style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
              <div className="d-flex align-items-center">
                <i className="ri-file-add-line fs-4 me-3"></i>
                <h5 className="mb-0 fw-bold">Request a Signature</h5>
              </div>
            </div>
            <Card.Body className="p-4">
              <Row className="g-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="ri-file-text-line me-2"></i>Document Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter document name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="form-control-lg shadow-sm border-2"
                      style={{ borderRadius: '12px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="ri-mail-line me-2"></i>Recipient Email
                    </Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter recipient's email"
                      value={createdForEmail}
                      onChange={(e) => setCreatedForEmail(e.target.value)}
                      className="form-control-lg shadow-sm border-2"
                      style={{ borderRadius: '12px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="ri-file-list-3-line me-2"></i>Description
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Enter document description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="shadow-sm border-2"
                      style={{ borderRadius: '12px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="ri-upload-cloud-2-line me-2"></i>Unsigned Document
                    </Form.Label>
                    <Form.Control 
                      type="file" 
                      onChange={(e) => handleFileUpload(e, setUnsignedFile)}
                      className="form-control-lg shadow-sm border-2"
                      style={{ borderRadius: '12px' }}
                    />
                    <Form.Text className="text-muted mt-2">
                      <i className="ri-information-line me-1"></i>
                      Supported formats: PDF, DOC, DOCX
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <div className="d-flex gap-3">
                    <Button 
                      variant="primary" 
                      onClick={handleUpload} 
                      disabled={uploading}
                      className="px-5 py-3 rounded-pill fw-semibold shadow-lg"
                    >
                      {uploading ? (
                        <>
                          <i className="ri-loader-4-line me-2"></i>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <i className="ri-upload-cloud-2-line me-2"></i>
                          Upload Document
                        </>
                      )}
                    </Button>
                  </div>
                  {uploading && (
                    <div className="mt-3">
                      <div className="d-flex justify-content-between mb-1">
                        <small className="text-muted">Upload Progress</small>
                        <small className="text-muted fw-semibold">{uploadProgress}%</small>
                      </div>
                      <ProgressBar
                        animated
                        now={uploadProgress}
                        className="shadow-sm"
                        style={{ height: '8px', borderRadius: '10px' }}
                      />
                    </div>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Enhanced Filter Section */}
        <Card className="border-0 shadow-lg rounded-4 mb-5">
          <div className="bg-gradient-warning text-white p-4 rounded-top-4" 
               style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <div className="d-flex align-items-center">
              <i className="ri-filter-line fs-4 me-3"></i>
              <h5 className="mb-0 fw-bold">Filter Documents</h5>
            </div>
          </div>
          <Card.Body className="p-4">
            <Row className="g-3">
              <Col md={4}>
                <FormControl
                  type="text"
                  placeholder="Filter documents by name"
                  className="form-control-lg shadow-sm border-2"
                  style={{ borderRadius: '12px' }}
                  onChange={(e) => setFilterQuery(e.target.value.toLowerCase())}
                />
              </Col>
              <Col md={4}>
                <FormControl
                  type="text"
                  placeholder="Filter by Created By"
                  className="form-control-lg shadow-sm border-2"
                  style={{ borderRadius: '12px' }}
                  onChange={(e) => setCreatedByFilter(e.target.value.toLowerCase())}
                />
              </Col>
              <Col md={4}>
                <FormControl
                  type="text"
                  placeholder="Filter by Created For"
                  className="form-control-lg shadow-sm border-2"
                  style={{ borderRadius: '12px' }}
                  onChange={(e) => setCreatedForFilter(e.target.value.toLowerCase())}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Enhanced Pending Documents */}
        <Card className="border-0 shadow-lg rounded-4 mb-5">
          <div className="bg-gradient-warning text-white p-4 rounded-top-4" 
               style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="ri-time-line fs-4 me-3"></i>
                <h5 className="mb-0 fw-bold">Pending Documents</h5>
              </div>
              <span className="badge bg-white text-warning px-3 py-2 rounded-pill">
                {pendingDocuments.length} pending
              </span>
            </div>
          </div>
          <Card.Body className="p-4">
            {pendingDocuments.length > 0 ? (
              <Row className="g-4">
                {pendingDocuments.map((doc) => (
                  <Col md={6} key={doc.id}>
                    <Card className="border-0 shadow-sm rounded-4 h-100"
                          style={{ 
                            transition: 'all 0.3s ease',
                            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                          }}>
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-center mb-3">
                          <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                            <i className="ri-file-text-line fs-4 text-warning"></i>
                          </div>
                          <div>
                            <h6 className="fw-bold mb-0">{doc.name}</h6>
                            <small className="text-muted">{doc.description}</small>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted small">Created By:</span>
                            <span className="fw-semibold small">{doc.createdBy.email}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted small">Created For:</span>
                            <span className="fw-semibold small">{doc.createdFor}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted small">Created At:</span>
                            <span className="fw-semibold small">
                              {new Date(doc.createdAt.seconds * 1000).toLocaleDateString()}
                            </span>
                          </div>
                          <Badge bg="warning" className="rounded-pill px-3 py-2">
                            <i className="ri-time-line me-1"></i>
                            {doc.signedStatus}
                          </Badge>
                        </div>

                        <Row className="g-3">
                          <Col xs={12}>
                            <div className="d-grid">
                              <a
                                href={doc.unsignedUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary btn-sm rounded-pill fw-semibold"
                              >
                                <i className="ri-eye-line me-2"></i>
                                View Unsigned Document
                              </a>
                            </div>
                          </Col>
                          <Col xs={12}>
                            <Form.Group>
                              <Form.Control
                                type="file"
                                onChange={(e) => handleFileUpload(e, setSignedFile)}
                                className="shadow-sm border-2"
                                style={{ borderRadius: '12px' }}
                              />
                            </Form.Group>
                            <Button
                              className="mt-2 w-100 rounded-pill fw-semibold"
                              onClick={() => handleSignedUpload(doc.id)}
                              disabled={uploading}
                            >
                              {uploading ? (
                                <>
                                  <i className="ri-loader-4-line me-2"></i>
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <i className="ri-upload-cloud-2-line me-2"></i>
                                  Upload Signed
                                </>
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="text-center py-5">
                <div className="mb-4">
                  <i className="ri-file-list-3-line text-muted" style={{ fontSize: '4rem' }}></i>
                </div>
                <h5 className="text-muted mb-3">No Pending Documents</h5>
                <p className="text-muted">All documents have been signed or no documents match your filters.</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Enhanced Completed Documents */}
        <Card className="border-0 shadow-lg rounded-4">
          <div className="bg-gradient-success text-white p-4 rounded-top-4" 
               style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="ri-check-double-line fs-4 me-3"></i>
                <h5 className="mb-0 fw-bold">Completed Documents</h5>
              </div>
              <span className="badge bg-white text-success px-3 py-2 rounded-pill">
                {completedDocuments.length} completed
              </span>
            </div>
          </div>
          <Card.Body className="p-4">
            {completedDocuments.length > 0 ? (
              <Row className="g-4">
                {completedDocuments.map((doc) => (
                  <Col md={6} key={doc.id}>
                    <Card className="border-0 shadow-sm rounded-4 h-100"
                          style={{ 
                            transition: 'all 0.3s ease',
                            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                          }}>
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-center mb-3">
                          <div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
                            <i className="ri-file-check-line fs-4 text-success"></i>
                          </div>
                          <div>
                            <h6 className="fw-bold mb-0">{doc.name}</h6>
                            <small className="text-muted">{doc.description}</small>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted small">Created By:</span>
                            <span className="fw-semibold small">{doc.createdBy.email}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted small">Created For:</span>
                            <span className="fw-semibold small">{doc.createdFor}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted small">Created At:</span>
                            <span className="fw-semibold small">
                              {new Date(doc.createdAt.seconds * 1000).toLocaleDateString()}
                            </span>
                          </div>
                          <Badge bg="success" className="rounded-pill px-3 py-2">
                            <i className="ri-check-line me-1"></i>
                            {doc.signedStatus}
                          </Badge>
                        </div>

                        <Row className="g-2">
                          <Col xs={6}>
                            <a
                              href={doc.unsignedUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-outline-primary btn-sm w-100 rounded-pill fw-semibold"
                            >
                              <i className="ri-file-line me-1"></i>
                              Original
                            </a>
                          </Col>
                          <Col xs={6}>
                            <a
                              href={doc.signedUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-success btn-sm w-100 rounded-pill fw-semibold"
                            >
                              <i className="ri-file-check-line me-1"></i>
                              Signed
                            </a>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="text-center py-5">
                <div className="mb-4">
                  <i className="ri-file-check-line text-muted" style={{ fontSize: '4rem' }}></i>
                </div>
                <h5 className="text-muted mb-3">No Completed Documents</h5>
                <p className="text-muted">No documents have been signed yet or no documents match your filters.</p>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </React.Fragment>
  );
}
